/**
 * The guard every screen's quiet refresh runs behind (FR-036): a background re-read must never
 * overwrite a change that landed while it was in flight. One implementation, shared by every store
 * that has a quiet refresh; `no-second-generation.spec.ts` fails if a store grows its own.
 *
 * An answer is discarded, and asked for again, in two cases:
 *
 * - **The data moved while it was being read.** `touched()` is how a store says so; `run()` takes
 *   the count before it reads and compares it afterwards.
 * - **A write was in flight at either end of the read.** A write does not always change the
 *   store's data on its way out — one whose follow-up read fails keeps its optimistic state and
 *   reports nothing — yet the answer may still predate that write on the server. So overlapping a
 *   write is a reason on its own, whether or not anything reported a change.
 *
 * The second ask happens at once when nothing is in flight any more, and otherwise when the write
 * settles (`settled()`), so it runs once and after the write it raced.
 *
 * **One read is in flight at a time.** Two signals can arrive for one event (the connection
 * returning, and the outbox emptying on the same answer), and two reads in flight can answer in
 * either order: the one asked first, answering last, would put the older list over the newer. A
 * signal that arrives during a read is remembered and asked once, afterwards, however many came.
 */
export interface QuietRefreshPort<T> {
  /** False while the screen has nothing ready to receive a background answer. */
  readonly ready: () => boolean;
  /** True while a local write is still in flight — a mismatch defers to `settled()` rather than
   *  retrying immediately, so the retry runs once, after the write it raced has actually finished. */
  readonly busy: () => boolean;
  /** The store's own single read — the same one `load()` (or its equivalent) uses. */
  readonly read: () => Promise<T>;
  /** Replaces the screen's data with a fresh, uncontested answer. */
  readonly apply: (data: T) => void;
  /**
   * 036 (issue 258, B4, FR-022) — called when `read()` answered successfully but the answer is
   * being discarded because a write overlapped it (`busy()`), the ONE case `readOnce()` defers to
   * `settled()` rather than re-asking at once. Optional: a port with no stored-view mark has nothing
   * to undo.
   *
   * `read()`'s own success may already have told a reader like `core/stored-views/storing-client.ts`
   * that its URL is live again — `clearShownFromStorage(url)` runs the instant the network answers,
   * before this guard ever gets a say, because storing-client has no idea a write is in flight. A
   * mark cleared on the strength of an answer nobody is about to show is exactly FR-022's "the mark
   * clears only when a screen applies the fresh body" turned backwards: this hook is where a port
   * that owns such a mark puts it back, mirroring `storing-client.ts`'s own `resolvedFromDeadline`
   * guard for the late-answer case — a mark must survive precisely because the fresh body it
   * describes is NOT what the screen is about to show.
   */
  readonly busyDiscard?: () => void;
}

export interface QuietRefresh {
  /** The quiet refresh itself — what a `resync`-keyed effect calls. */
  readonly run: () => Promise<void>;
  /** Bumps the generation. Every local change to the store's data calls this — a fresh `load()`, a
   *  write beginning or settling, an edit or a capture folded in, a reorder, a merge. */
  readonly touched: () => void;
  /** Runs the deferred follow-up refresh a mismatch left behind, if one is still owed and nothing is
   *  busy any more. A no-op otherwise — safe to call after every write settles. */
  readonly settled: () => void;
  /** Test seam, like every other module-level store's reset. */
  readonly reset: () => void;
}

export function createQuietRefresh<T>(port: QuietRefreshPort<T>): QuietRefresh {
  let generation = 0;
  let owedFollowUp = false;
  let inFlight: Promise<void> | null = null;
  let askedDuringTheRead = false;

  function touched(): void {
    generation += 1;
  }

  function run(): Promise<void> {
    if (inFlight) {
      askedDuringTheRead = true;
      return inFlight;
    }
    const mine = readOnce().finally(() => {
      if (inFlight === mine) inFlight = null;
      if (!askedDuringTheRead) return;
      askedDuringTheRead = false;
      void run();
    });
    inFlight = mine;
    return mine;
  }

  async function readOnce(): Promise<void> {
    if (!port.ready()) return;
    const myGeneration = generation;
    const beganDuringAWrite = port.busy();
    let data: T;
    try {
      data = await port.read();
    } catch {
      // Quiet, deliberately: a background read the practitioner never asked for fails silently.
      // The data already on screen stays exactly as correct as it was a moment ago.
      return;
    }
    if (!port.ready()) return; // the screen could have left `ready` mid-read
    if (generation !== myGeneration || beganDuringAWrite || port.busy()) {
      // Something these answers cannot be trusted to have seen: applying them could silently roll
      // the screen back to what it looked like before that change.
      if (port.busy()) {
        owedFollowUp = true;
        // 036 (issue 258, B4) — `busy()` is STILL true right now: a write is unconfirmed, this
        // answer predates it, and the retry `settled()` will eventually run is the only one that
        // may actually reach the screen. `read()`'s own success may already have cleared a mark
        // this port owns on the strength of an answer that is not about to be shown — this is where
        // that gets put back.
        port.busyDiscard?.();
      } else void run();
      return;
    }
    port.apply(data);
  }

  function settled(): void {
    if (!owedFollowUp || port.busy()) return;
    owedFollowUp = false;
    void run();
  }

  function reset(): void {
    generation = 0;
    owedFollowUp = false;
    inFlight = null;
    askedDuringTheRead = false;
  }

  return { run, touched, settled, reset };
}
